import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select, { type SelectProps } from '@mui/material/Select'
import Stack, { type StackProps } from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { type ChangeEvent } from 'react'

export interface TablePaginationProps extends StackProps {
  /**
   * The total number of rows.
   */
  count?: number
  /**
   * The number of rows to display per page.
   */
  pageSize?: number
  /**
   * The zero-based index of the current page.
   */
  pageIndex?: number
  /**
   * The number of rows to display per page.
   * @default [10, 20, 30, 40, 50, 500, 1000]
   */
  pageSizeOptions?: number[]
  /**
   * If `true`, the pagination component will be disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * Callback fired when the page is changed.
   */
  onPageChange?: (pageIndex: number) => void
  /**
   * Callback fired when the number of rows per page is changed.
   */
  onRowsPerPageChange?: (pageSize: number) => void
}

export const TablePagination = (props: TablePaginationProps) => {
  const {
    count = 0,
    pageSize = 10,
    pageIndex = 0,
    pageSizeOptions = [10, 20, 30, 40, 50, 500, 1000],
    disabled,
    onPageChange,
    onRowsPerPageChange,
    ...rest
  } = props
  const totalPages = Math.ceil(count / pageSize)
  const lastPageIndex = totalPages - 1

  const handlePreviousButtonClick = () => {
    onPageChange?.(pageIndex - 1)
  }

  const handleNextPageButtonClick = () => {
    onPageChange?.(pageIndex + 1)
  }

  const handlePageInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newPage = Number(event.target.value) - 1
    if (newPage >= 0 && newPage <= lastPageIndex) {
      onPageChange?.(newPage)
    }
  }

  const handleRowsPerPageChange: SelectProps['onChange'] = (event) => {
    const newRowsPerPage = Number(event.target.value)
    onPageChange?.(0)
    onRowsPerPageChange?.(newRowsPerPage)
  }

  return (
    <Stack direction="row" alignItems="center" gap={1} {...rest}>
      <Button
        disabled={disabled || pageIndex === 0}
        onClick={handlePreviousButtonClick}
        sx={{ flex: 1 }}
      >
        Previous
      </Button>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2">Page</Typography>
        <OutlinedInput
          size="small"
          type="number"
          value={pageIndex + 1}
          onChange={handlePageInputChange}
          inputProps={{
            sx: {
              'width': 54,
              'textAlign': 'center',
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
            },
          }}
        />
        <Typography variant="body2">of</Typography>
        <Typography variant="body2" fontWeight={700}>{totalPages}</Typography>
        <Select size="small" value={pageSize} onChange={handleRowsPerPageChange}>
          {
            pageSizeOptions.map(el => (
              <MenuItem key={el} value={el}>
                {el}
                {' '}
                rows
              </MenuItem>
            ))
          }
        </Select>
      </Stack>
      <Button
        disabled={disabled || pageIndex >= lastPageIndex}
        onClick={handleNextPageButtonClick}
        sx={{ flex: 1 }}
      >
        Next
      </Button>
    </Stack>
  )
}
